/**
 * GraphQL Client - Apollo Client wrapper
 *
 * Provides a centralized GraphQL client for all modules
 * Extends existing Apollo client configuration from the main app
 */

import { ApolloClient, InMemoryCache, HttpLink, from, type NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;

/**
 * Create error handling link
 */
const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
        });
    }

    if (networkError) {
        console.error(`[Network error]: ${networkError}`);
    }
});

/**
 * Create HTTP link
 */
const createHttpLink = () => {
    return new HttpLink({
        uri: '/api/v2/graphql',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * Create Apollo Client instance
 *
 * @returns Apollo Client instance
 */
const createApolloClient = (): ApolloClient<NormalizedCacheObject> => {
    return new ApolloClient({
        ssrMode: typeof window === 'undefined',
        link: from([errorLink, createHttpLink()]),
        cache: new InMemoryCache({
            typePolicies: {
                Query: {
                    fields: {
                        cart: {
                            merge: true,
                        },
                        customer: {
                            merge: true,
                        },
                    },
                },
                Cart: {
                    keyFields: ['id'],
                    fields: {
                        items: {
                            merge(_, incoming) {
                                return incoming;
                            },
                        },
                    },
                },
                CartItem: {
                    keyFields: ['uid'],
                },
                CustomerAddress: {
                    keyFields: ['id'],
                },
            },
        }),
        defaultOptions: {
            watchQuery: {
                fetchPolicy: 'cache-and-network',
                nextFetchPolicy: 'cache-first',
            },
            query: {
                fetchPolicy: 'cache-first',
                errorPolicy: 'all',
            },
            mutate: {
                errorPolicy: 'all',
            },
        },
    });
};

/**
 * Get or create Apollo Client
 *
 * @param initialState - Optional initial cache state for hydration
 * @returns Apollo Client instance
 *
 * @example
 * ```typescript
 * import { getGraphQLClient } from '@kidslabs/module-core';
 *
 * const client = getGraphQLClient();
 * const result = await client.query({ query: MY_QUERY });
 * ```
 */
export const getGraphQLClient = (
    initialState: NormalizedCacheObject | null = null
): ApolloClient<NormalizedCacheObject> => {
    const _apolloClient = apolloClient ?? createApolloClient();

    // Hydrate the cache with initial state if provided
    if (initialState) {
        const existingCache = _apolloClient.extract();
        _apolloClient.cache.restore({
            ...existingCache,
            ...initialState,
        });
    }

    // For SSR always create a new Apollo Client
    if (typeof window === 'undefined') {
        return _apolloClient;
    }

    // Create Apollo Client once on client side
    if (!apolloClient) {
        apolloClient = _apolloClient;
    }

    return _apolloClient;
};

/**
 * GraphQL client instance
 * Use getGraphQLClient() for better control over initialization
 */
export const graphqlClient = getGraphQLClient();

export default graphqlClient;
